import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageSquare, History, Clock, User, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Layout } from '@/components/wiki/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HistoryPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pageResponse = await axios.get(`${API}/pages/${slug}`);
        setPage(pageResponse.data);
        
        const historyResponse = await axios.get(`${API}/pages/${slug}/history`);
        setHistory(historyResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-[#242424] rounded w-1/3" />
            <div className="h-20 bg-[#242424] rounded" />
            <div className="h-20 bg-[#242424] rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Page Header */}
      <div className="bg-[#0F0F0F] border-b border-[#333333]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Tabs defaultValue="history" className="w-auto">
              <TabsList className="bg-[#242424]">
                <TabsTrigger 
                  value="article" 
                  onClick={() => window.location.href = `/wiki/${slug}`}
                  className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1A1A1A] text-gray-300"
                >
                  Article
                </TabsTrigger>
                <TabsTrigger 
                  value="talk" 
                  onClick={() => window.location.href = `/wiki/${slug}/talk`}
                  className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1A1A1A] text-gray-300"
                >
                  <MessageSquare className="h-4 w-4 mr-1" />
                  Talk
                </TabsTrigger>
                <TabsTrigger value="history" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1A1A1A] text-gray-300" data-testid="history-tab-active">
                  <History className="h-4 w-4 mr-1" />
                  History
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="history-page">
        {/* Header */}
        <div className="mb-6">
          <Link to={`/wiki/${slug}`} className="text-sm text-[#D4AF37] hover:underline flex items-center gap-1 mb-2">
            <ArrowLeft className="h-4 w-4" />
            Back to {page?.title}
          </Link>
          <h1 className="font-spectral text-2xl font-bold text-white">
            Revision History: {page?.title}
          </h1>
          <p className="text-gray-400 mt-1">
            View all changes made to this article over time.
          </p>
        </div>

        {/* History List */}
        <div className="bg-[#242424] rounded-xl border border-[#333333] overflow-hidden">
          {history.length === 0 ? (
            <div className="p-12 text-center">
              <History className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <h3 className="font-spectral text-xl font-semibold text-white mb-2">No revision history</h3>
              <p className="text-gray-400">This page has no recorded changes yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#333333]">
              {history.map((version, index) => (
                <div 
                  key={version.id} 
                  className="p-4 hover:bg-[#1A1A1A] transition-colors"
                  data-testid={`history-version-${version.version}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#1A1A1A] text-sm font-medium flex-shrink-0">
                        {version.edited_by_username[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-white">{version.edited_by_username}</span>
                          {index === 0 && (
                            <span className="px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] text-xs rounded-full font-medium">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 mb-1">
                          {version.edit_summary || 'No edit summary'}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(version.created_at).toLocaleString()}
                          </span>
                          <span>Version {version.version}</span>
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedVersion(version)}
                      className="border-[#333333] text-gray-300 hover:bg-[#333333] hover:text-white"
                      data-testid={`view-version-${version.version}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Version Preview Dialog */}
        <Dialog open={!!selectedVersion} onOpenChange={() => setSelectedVersion(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-[#242424] border-[#333333]">
            <DialogHeader>
              <DialogTitle className="font-spectral text-white">
                Version {selectedVersion?.version} - {new Date(selectedVersion?.created_at).toLocaleString()}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-4 text-sm text-gray-400">
                <User className="h-4 w-4" />
                <span>Edited by {selectedVersion?.edited_by_username}</span>
                <span className="text-gray-400">•</span>
                <span>{selectedVersion?.edit_summary}</span>
              </div>
              <div 
                className="wiki-content border border-[#333333] rounded-lg p-6 bg-[#1A1A1A]"
                dangerouslySetInnerHTML={{ __html: selectedVersion?.content }}
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
